import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faCog,
    faImage,
    faToggleOn,
    faFilter,
    faTable,
    faSave,
} from '@fortawesome/free-solid-svg-icons';
import config from '../configs/config';
import './styles/RowColumnProperties.css';

// Turn "3-5, 9" into 0-based indexes [2,3,4,8]. Free text, so there's no
// invalid intermediate state to fight with like the old number fields had —
// whatever doesn't parse yet is just ignored until the user finishes typing.
const parseRowNumbers = (text) => {
    const set = new Set();
    (text || '').split(',').forEach((part) => {
        const trimmed = part.trim();
        if (!trimmed) return;
        const rangeMatch = trimmed.match(/^(\d+)\s*-\s*(\d+)$/);
        if (rangeMatch) {
            let a = parseInt(rangeMatch[1], 10);
            let b = parseInt(rangeMatch[2], 10);
            if (a > b) [a, b] = [b, a];
            for (let n = a; n <= b; n++) if (n >= 1) set.add(n - 1);
            return;
        }
        const n = parseInt(trimmed, 10);
        if (!isNaN(n) && n >= 1) set.add(n - 1);
    });
    return [...set].sort((a, b) => a - b);
};

// Inverse of parseRowNumbers, for seeding the field when a header is opened.
const formatRowNumbers = (indexes) => {
    if (!indexes || indexes.length === 0) return '';
    const sorted = [...indexes].sort((a, b) => a - b);
    const parts = [];
    let start = sorted[0];
    let prev = sorted[0];
    for (let i = 1; i <= sorted.length; i++) {
        const cur = sorted[i];
        if (cur === prev + 1) {
            prev = cur;
            continue;
        }
        parts.push(start === prev ? `${start + 1}` : `${start + 1}-${prev + 1}`);
        start = cur;
        prev = cur;
    }
    return parts.join(', ');
};

// Checkbox pill: same native checkbox + label wiring as before (identical
// onChange contract), just presented as a toggle chip like the row badges.
const CheckPill = ({ id, label, checked, onChange }) => (
    <label className={`rcp-check${checked ? ' rcp-check-on' : ''}`} htmlFor={id}>
        <input
            type="checkbox"
            id={id}
            checked={checked || false}
            onChange={onChange}
        />
        {label}
    </label>
);

const RowColumnProperties = ({ isOpen, onClose, item, index, handlePropertyChange, qType }) => {
    // Row Numbers is edited as free text and parsed/committed on every
    // change, but the box itself always shows exactly what was typed rather
    // than a value derived back from the model — so a still-incomplete
    // range like "3-" never gets fought or overwritten mid-edit.
    const [rowNumbersText, setRowNumbersText] = useState('');
    const [imgUploading, setImgUploading] = useState(false);
    // Full-size image lightbox (click the small preview to open).
    const [imgZoomOpen, setImgZoomOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        // Seed from the item's current indexes regardless of whether it's a
        // header yet — the Header checkbox can be turned on for any row, so
        // this needs to be ready the moment the field appears.
        setRowNumbersText(formatRowNumbers(item?.indexes));
        setImgZoomOpen(false); // never carry an open lightbox to another item
        // Re-sync whenever a different item is opened, not on every keystroke.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, item?.id, index]);

    if (!isOpen) return null;

    const handleImageFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }
        if (file.size > 3 * 1024 * 1024) {
            alert('Image must be under 3MB');
            return;
        }

        setImgUploading(true);
        try {
            // Upload to the backend (SURVEY_IMAGES folder, server-generated
            // filename) and store the returned URL - NOT a base64 data URL,
            // which used to bloat sqgroup_json and couldn't be reused as a
            // plain link by the PSNST survey pages.
            const formData = new FormData();
            formData.append('image', file);
            const response = await fetch(`${config.API_BASE_URL}/upload-survey-image`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });
            const data = await response.json();
            if (!response.ok || !data.success || !data.url) {
                throw new Error(data.error || 'Upload failed');
            }
            handlePropertyChange(index, 'imageURL', data.url);
        } catch (err) {
            console.error('Image upload failed:', err);
            alert(`Failed to upload image: ${err.message}`);
        } finally {
            setImgUploading(false);
            e.target.value = ''; // allow re-selecting the same file later
        }
    };

    return (
        <div className="rcp-overlay" onClick={() => onClose()}>
            <div className="rcp-content" onClick={(e) => e.stopPropagation()}>
                <div className="rcp-header">
                    <h3>
                        <FontAwesomeIcon icon={faCog} style={{ color: '#6366f1' }} />
                        {item?.header ? 'Header Properties' : 'Row/Column Properties'}
                    </h3>
                    <button className="rcp-close-btn" title="Close" onClick={() => onClose()}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="rcp-body">
                    {/* ── Basics ── */}
                    <div className="rcp-section">
                        <h4 className="rcp-section-title">Basics</h4>
                        <div className="rcp-grid">
                            <div className="rcp-field">
                                <label htmlFor="index">Index</label>
                                <input
                                    type="text"
                                    id="index"
                                    value={item?.index || ''}
                                    onChange={(e) => handlePropertyChange(index, 'index', e.target.value)}
                                />
                            </div>
                            <div className="rcp-field">
                                <label htmlFor="text">Label</label>
                                <input
                                    type="text"
                                    id="text"
                                    value={item?.text || ''}
                                    onChange={(e) => handlePropertyChange(index, 'text', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Image ── */}
                    <div className="rcp-section">
                        <h4 className="rcp-section-title">
                            <FontAwesomeIcon icon={faImage} /> Image
                        </h4>
                        <div className="rcp-grid">
                            <div className="rcp-field">
                                <label htmlFor="imageURL">Image URL</label>
                                <input
                                    type="text"
                                    id="imageURL"
                                    placeholder="https://example.com/image.png"
                                    value={item?.imageURL || ''}
                                    onChange={(e) => handlePropertyChange(index, 'imageURL', e.target.value)}
                                />
                            </div>
                            <div className="rcp-field">
                                <label htmlFor="imageUpload">Or Upload Image</label>
                                <input
                                    type="file"
                                    id="imageUpload"
                                    accept="image/*"
                                    onChange={handleImageFileChange}
                                    disabled={imgUploading}
                                />
                                {imgUploading && <span className="rcp-uploading">Uploading...</span>}
                            </div>
                        </div>
                        {item?.imageURL && (
                            <div className="rcp-img-preview">
                                <img
                                    src={item.imageURL}
                                    alt="preview"
                                    title="Click to zoom"
                                    onClick={() => setImgZoomOpen(true)}
                                />
                                <span className="rcp-img-zoom-hint">Click image to zoom</span>
                                <button
                                    type="button"
                                    className="rcp-img-remove-btn"
                                    title="Remove image"
                                    onClick={() => handlePropertyChange(index, 'imageURL', '')}
                                >
                                    Remove Image
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ── Behavior ── */}
                    <div className="rcp-section">
                        <h4 className="rcp-section-title">
                            <FontAwesomeIcon icon={faToggleOn} /> Behavior
                        </h4>
                        <div className="rcp-checks">
                            <CheckPill
                                id="header"
                                label="Header"
                                checked={item?.header}
                                onChange={(e) => handlePropertyChange(index, 'header', e.target.checked)}
                            />

                            {!item?.header && (<>
                                {/* Single Response answers are inherently exclusive
                                    (only one can be picked), so the checkbox is
                                    redundant and hidden for that question type. */}
                                {qType !== "SingleResponse" && (
                                    <CheckPill
                                        id="exclusive"
                                        label="Exclusive"
                                        checked={item?.exclusive}
                                        onChange={(e) => handlePropertyChange(index, 'exclusive', e.target.checked)}
                                    />
                                )}
                                <CheckPill
                                    id="screenout"
                                    label="Screen Out"
                                    checked={item?.screenout}
                                    onChange={(e) => handlePropertyChange(index, 'screenout', e.target.checked)}
                                />
                                <CheckPill
                                    id="other"
                                    label="Other"
                                    checked={item?.other}
                                    onChange={(e) => handlePropertyChange(index, 'other', e.target.checked)}
                                />
                            </>)}
                            <CheckPill
                                id="fix"
                                label="Fix"
                                checked={item?.fix}
                                onChange={(e) => handlePropertyChange(index, 'fix', e.target.checked)}
                            />

                        </div>

                        {item?.header && (
                            <>
                                <div className="rcp-field" style={{ marginTop: '12px' }}>
                                    <label htmlFor="header-rownumbers">Row Numbers</label>
                                    <input
                                        type="text"
                                        id="header-rownumbers"
                                        placeholder="e.g. 3-5, 9"
                                        value={rowNumbersText}
                                        onChange={(e) => {
                                            setRowNumbersText(e.target.value);
                                            handlePropertyChange(index, 'indexes', parseRowNumbers(e.target.value));
                                        }}
                                    />
                                </div>
                                <p className="rcp-hint">
                                    Row numbers refer to the current row order (1 = first row). List every row that
                                    belongs to this header, comma-separated — single numbers ("3, 9") or ranges
                                    ("3-5, 9") both work, and rows don't need to be contiguous. To nest a header inside
                                    another, give it a set of row numbers that's entirely covered by the outer header's
                                    set. Note: a row marked as Header is used as a label only — it is no longer exported
                                    as its own answer option, so its Index/Label text becomes the header text instead.
                                </p>
                            </>
                        )}

                        {item?.other && (
                            <div className="rcp-field" style={{ marginTop: '12px' }}>
                                <label htmlFor="otherPiping">Other Piping</label>
                                <input
                                    type="text"
                                    id="otherPiping"
                                    value={item?.otherPiping || ''}
                                    onChange={(e) => handlePropertyChange(index, 'otherPiping', e.target.value)}
                                />
                            </div>
                        )}
                    </div>

                    {/* ── Logic & Quotas ── */}
                    {!item?.header && (
                        <div className="rcp-section">
                            <h4 className="rcp-section-title">
                                <FontAwesomeIcon icon={faFilter} /> Logic &amp; Quotas
                            </h4>
                            <div className="rcp-grid">
                                <div className="rcp-field">
                                    <label htmlFor="quotaCounts">Quota Counts</label>
                                    <input
                                        type="text"
                                        id="quotaCounts"
                                        value={item?.quotaCounts || ''}
                                        onChange={(e) => handlePropertyChange(index, 'quotaCounts', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <label htmlFor="filterLogic">Filter Logic</label>
                                    <input
                                        type="text"
                                        id="filterLogic"
                                        value={item?.filterLogic || ''}
                                        onChange={(e) => handlePropertyChange(index, 'filterLogic', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <label htmlFor="termLogic">Termination Logic</label>
                                    <input
                                        type="text"
                                        id="termLogic"
                                        value={item?.termLogic || ''}
                                        onChange={(e) => handlePropertyChange(index, 'termLogic', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <label htmlFor="punchLogic">Punch Logic</label>
                                    <input
                                        type="text"
                                        id="punchLogic"
                                        value={item?.punchLogic || ''}
                                        onChange={(e) => handlePropertyChange(index, 'punchLogic', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Custom Rows extras ── */}
                    {qType == "CustomRows" && (
                        <div className="rcp-section">
                            <h4 className="rcp-section-title">
                                <FontAwesomeIcon icon={faTable} /> Custom Row Settings
                            </h4>
                            <div className="rcp-grid">
                                <div className="rcp-field">
                                    <label htmlFor="Pretext">Pretext</label>
                                    <input
                                        type="text"
                                        id="Pretext"
                                        value={item?.Pretext || ''}
                                        onChange={(e) => handlePropertyChange(index, 'Pretext', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <label htmlFor="Posttext">Posttext</label>
                                    <input
                                        type="text"
                                        id="Posttext"
                                        value={item?.Posttext || ''}
                                        onChange={(e) => handlePropertyChange(index, 'Posttext', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <label htmlFor="min">Min</label>
                                    <input
                                        type="text"
                                        id="min"
                                        value={item?.min || ''}
                                        onChange={(e) => handlePropertyChange(index, 'min', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field">
                                    <label htmlFor="max">Max</label>
                                    <input
                                        type="text"
                                        id="max"
                                        value={item?.max || ''}
                                        onChange={(e) => handlePropertyChange(index, 'max', e.target.value)}
                                    />
                                </div>
                                <div className="rcp-field rcp-field-full">
                                    <label htmlFor="type">Type</label>
                                    <select
                                        id="type"
                                        value={item?.type || ''}
                                        onChange={(e) => handlePropertyChange(index, 'type', e.target.value)}
                                    >
                                        <option value="">Select type</option>
                                        <option value="radio">Radio</option>
                                        <option value="multi">Multi</option>
                                        <option value="freeze">Freeze</option>
                                        <option value="open_numeric_D">Open Numeric Decimal</option>
                                        <option value="open_numeric">Open Numeric</option>
                                        <option value="open_string">Open String</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Custom Cols extras ── */}
                    {qType == "CustomCols" && (
                        <div className="rcp-section">
                            <h4 className="rcp-section-title">
                                <FontAwesomeIcon icon={faTable} /> Custom Column Settings
                            </h4>
                            <div className="rcp-grid">
                                <div className="rcp-field rcp-field-full">
                                    <label htmlFor="colType">Column Type</label>
                                    <select
                                        id="colType"
                                        value={item?.type || ''}
                                        onChange={(e) => handlePropertyChange(index, 'type', e.target.value)}
                                    >
                                        <option value="">Select type</option>
                                        <option value="String">String</option>
                                        <option value="Numeric">Numeric</option>
                                        <option value="SingleResponse">Single Response</option>
                                        <option value="MultiResponse">Multi Response</option>
                                        <option value="single2dgrid">Single 2D Grid</option>
                                        <option value="multigrid">Multi Grid</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer - same layout/buttons as RedirectModal. Edits in this
                    popup apply to the row live as you type, so Save here just
                    confirms & closes; the survey itself is persisted by the
                    builder's Save Survey button, exactly as before. */}
                <div className="rcp-footer">
                    <button className="rcp-cancel-btn" onClick={() => onClose()}>
                        Close
                    </button>
                    <button className="rcp-save-btn" onClick={() => onClose()}>
                        <FontAwesomeIcon icon={faSave} /> Save
                    </button>
                </div>
            </div>

            {/* Image lightbox - above the popup; click anywhere (or the ✕) to close */}
            {imgZoomOpen && item?.imageURL && (
                <div className="rcp-zoom-overlay" onClick={(e) => { e.stopPropagation(); setImgZoomOpen(false); }}>
                    <button className="rcp-zoom-close-btn" title="Close zoom">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <img src={item.imageURL} alt="full size preview" />
                </div>
            )}
        </div>
    );
};

export default RowColumnProperties;
